import { z } from 'zod'

import { BlockSchema } from './blocks'
import { InquiryTrackingSchema } from './tracking'
import { PackagingDetailsSchema } from './catalog'
import { PriceSchema, ShopProductSchema, SHOP_LIMITS, SizeGuideSchema, VariantInputSchema, emptyShopProduct, emptySizeGuide } from './shop'
import { BrandingSchema, DEFAULT_BRANDING, DEFAULT_TEMPLATE_ID, TemplateIdSchema } from './templates'
import {
  FaqSchema,
  IdSchema,
  LinkSchema,
  LOCALES,
  LocaleSchema,
  LocalizedMediaListSchema,
  LocalizedMediaSchema,
  LocalizedSchema,
  PublishStatusSchema,
  SeoSchema,
  SlugSchema,
  paginated,
} from './common'

// Admin write contracts (input) and admin read shapes (output). Rich-text
// fields are `*Html` Localized strings; the API sanitizes them on write.

const timestamps = {
  createdAt: z.string(),
  updatedAt: z.string(),
}

// ── Pages ────────────────────────────────────────────────────────────────
export const PageInputSchema = z.object({
  slug: SlugSchema,
  title: LocalizedSchema,
  status: PublishStatusSchema,
  blocks: z.array(BlockSchema).max(60),
  seo: SeoSchema,
})
export const PageSchema = PageInputSchema.extend({ id: z.string(), publishedAt: z.string().nullable(), ...timestamps })
export type PageInput = z.infer<typeof PageInputSchema>
export type Page = z.infer<typeof PageSchema>

// ── Product categories ───────────────────────────────────────────────────
export const ProductCategoryInputSchema = z.object({
  slug: SlugSchema,
  sortOrder: z.number().int().min(0).max(10_000),
  status: PublishStatusSchema,
  title: LocalizedSchema,
  tagline: LocalizedSchema,
  summary: LocalizedSchema,
  heroImage: LocalizedMediaSchema,
  gallery: LocalizedMediaListSchema,
  bodyHtml: LocalizedSchema,
  details: PackagingDetailsSchema,
  faqs: z.array(FaqSchema).max(40),
  seo: SeoSchema,
  /** Online store (flag `store`): the size chart the category's product pages open. */
  sizeGuide: SizeGuideSchema.default(emptySizeGuide),
})
export const ProductCategorySchema = ProductCategoryInputSchema.extend({ id: z.string(), ...timestamps })
export type ProductCategoryInput = z.infer<typeof ProductCategoryInputSchema>
export type ProductCategory = z.infer<typeof ProductCategorySchema>

// ── Products ─────────────────────────────────────────────────────────────
export const ProductInputSchema = z.object({
  slug: SlugSchema,
  categoryId: IdSchema,
  sortOrder: z.number().int().min(0).max(10_000),
  status: PublishStatusSchema,
  title: LocalizedSchema,
  summary: LocalizedSchema,
  gallery: LocalizedMediaListSchema,
  bodyHtml: LocalizedSchema,
  details: PackagingDetailsSchema,
  faqs: z.array(FaqSchema).max(40),
  industryIds: z.array(IdSchema).max(20),
  seo: SeoSchema,
  // Online store (flag `store`). A product without a price is not for sale and stays out of the store.
  price: PriceSchema.nullable().default(null),
  /** The original price, shown struck through: a sale. Must be higher than `price`. */
  compareAtPrice: PriceSchema.nullable().default(null),
  shop: ShopProductSchema.default(emptyShopProduct),
  /** Colour × size cells with SKU and stock; stored in product_variants, replaced as a whole on save. */
  variants: z.array(VariantInputSchema).max(SHOP_LIMITS.variants).default([]),
})
export const ProductSchema = ProductInputSchema.extend({ id: z.string(), publishedAt: z.string().nullable(), ...timestamps })
export type ProductInput = z.infer<typeof ProductInputSchema>
export type Product = z.infer<typeof ProductSchema>

// ── Industries ───────────────────────────────────────────────────────────
export const IndustryInputSchema = z.object({
  slug: SlugSchema,
  sortOrder: z.number().int().min(0).max(10_000),
  status: PublishStatusSchema,
  title: LocalizedSchema,
  summary: LocalizedSchema,
  risk: LocalizedSchema,
  heroImage: LocalizedMediaSchema,
  gallery: LocalizedMediaListSchema,
  bodyHtml: LocalizedSchema,
  details: PackagingDetailsSchema,
  recommendedCategoryIds: z.array(IdSchema).max(20),
  recommendedProductIds: z.array(IdSchema).max(30),
  faqs: z.array(FaqSchema).max(40),
  seo: SeoSchema,
})
export const IndustrySchema = IndustryInputSchema.extend({ id: z.string(), ...timestamps })
export type IndustryInput = z.infer<typeof IndustryInputSchema>
export type Industry = z.infer<typeof IndustrySchema>

// ── Articles (journal + news) ────────────────────────────────────────────
export const ArticleSectionSchema = z.enum(['journal', 'news'])
export type ArticleSection = z.infer<typeof ArticleSectionSchema>

export const ArticleInputSchema = z.object({
  slug: SlugSchema,
  section: ArticleSectionSchema,
  status: PublishStatusSchema,
  title: LocalizedSchema,
  category: LocalizedSchema,
  excerpt: LocalizedSchema,
  heroImage: LocalizedMediaSchema,
  bodyHtml: LocalizedSchema,
  author: z.string().max(120),
  featured: z.boolean(),
  faqs: z.array(FaqSchema).max(40),
  publishedAt: z.string().nullable(),
  seo: SeoSchema,
})
export const ArticleSchema = ArticleInputSchema.extend({ id: z.string(), ...timestamps })
export type ArticleInput = z.infer<typeof ArticleInputSchema>
export type Article = z.infer<typeof ArticleSchema>

/** Compact row for admin list tables. */
export const ContentListItemSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: LocalizedSchema,
  status: PublishStatusSchema,
  updatedAt: z.string(),
  createdAt: z.string().optional(),
  /** Articles: when it is (or was) published; a future date means scheduled. */
  publishedAt: z.string().nullable().optional(),
  meta: z.string().nullable(),
  /** Whether each locale has its own title (list badges). */
  translations: z.object({ en: z.boolean(), id: z.boolean(), zh: z.boolean(), vi: z.boolean().default(false), ja: z.boolean().default(false) }),
})
export type ContentListItem = z.infer<typeof ContentListItemSchema>
export const ContentListResponseSchema = paginated(ContentListItemSchema)

// ── Local search and lead form ───────────────────────────────────────────
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type Weekday = (typeof WEEKDAYS)[number]
const TimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM')

/** One entry per open day; days without an entry are closed. */
export const OpeningHoursSchema = z
  .array(z.object({ day: z.enum(WEEKDAYS), opens: TimeSchema, closes: TimeSchema }))
  .max(7)
  .default([])
  .refine((h) => new Set(h.map((x) => x.day)).size === h.length, 'Each day can only be listed once')
export type OpeningHours = z.infer<typeof OpeningHoursSchema>

/** Feeds the `LocalBusiness` structured data. It is only emitted when the address has a city. */
export const LocalBusinessSchema = z.object({
  /** A schema.org type such as `LocalBusiness`, `Store` or `ProfessionalService`. */
  type: z.string().regex(/^[A-Z][A-Za-z]{2,40}$/, 'A schema.org type name').default('LocalBusiness'),
  priceRange: z.string().max(20).default(''),
  geo: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable().default(null),
  hours: OpeningHoursSchema,
})
export type LocalBusiness = z.infer<typeof LocalBusinessSchema>
export const DEFAULT_LOCAL_BUSINESS: LocalBusiness = { type: 'LocalBusiness', priceRange: '', geo: null, hours: [] }

export const FIELD_MODES = ['hidden', 'optional', 'required'] as const
const FieldModeSchema = z.enum(FIELD_MODES)
export const INQUIRY_FIELD_KEYS = ['company', 'role', 'country', 'productInterest', 'quantity', 'timeline', 'message'] as const
export type InquiryFieldKey = (typeof INQUIRY_FIELD_KEYS)[number]

/** Name and email are always asked. Every other field of the quote form can be hidden, optional or required. */
export const InquiryFieldsSchema = z.object({
  company: FieldModeSchema,
  role: FieldModeSchema,
  country: FieldModeSchema,
  productInterest: FieldModeSchema,
  quantity: FieldModeSchema,
  timeline: FieldModeSchema,
  message: FieldModeSchema,
})
export type InquiryFields = z.infer<typeof InquiryFieldsSchema>
/** The form as it was before it became configurable. */
export const DEFAULT_INQUIRY_FIELDS: InquiryFields = { company: 'optional', role: 'optional', country: 'required', productInterest: 'required', quantity: 'required', timeline: 'required', message: 'required' }

/** The site's own wording for the swatch library (kind `material`/`finish`, fixed for every site). Empty fields fall back to the built-in defaults. */
export const SwatchNamesSchema = z.object({
  plural: LocalizedSchema.default({}),
  material: LocalizedSchema.default({}),
  finish: LocalizedSchema.default({}),
})
export type SwatchNames = z.infer<typeof SwatchNamesSchema>
export const DEFAULT_SWATCH_NAMES: SwatchNames = { plural: {}, material: {}, finish: {} }

// ── Site settings (singleton documents) ──────────────────────────────────
export const NavItemSchema = LinkSchema.extend({
  /** Dropdown entries. For a mega menu they fill its last column. */
  children: z.array(LinkSchema.extend({ description: LocalizedSchema })).max(30),
  /** Products-style mega menu: product categories and industries, plus `children` under `megaHeading`. */
  mega: z.boolean().default(false),
  megaHeading: LocalizedSchema.default({}),
})

export const SiteSettingsSchema = z
  .object({
  /** Fallback language for every translatable field. Changeable by admins. */
  primaryLocale: LocaleSchema,
  /** Languages the site is published in. Must include the primary. How many is capped by the plan (`languages` limit), checked at save. */
  enabledLocales: z.array(LocaleSchema).min(1).max(LOCALES.length),
  siteName: z.string().min(1).max(120),
  titleTemplate: z.string().max(120),
  defaultTitle: LocalizedSchema,
  defaultDescription: LocalizedSchema,
  defaultOgImage: LocalizedMediaSchema,
  tagline: LocalizedSchema,
  legalName: z.string().max(200),
  foundingYear: z.number().int().min(1800).max(2100).nullable(),
  address: z.object({
    street: z.string().max(200),
    city: z.string().max(120),
    region: z.string().max(120),
    postalCode: z.string().max(20),
    country: z.string().max(120),
  }),
  phone: z.string().max(40),
  email: z.string().max(254),
  /** Who is emailed for each new quote request. Empty falls back to `email`. */
  inquiryRecipients: z.array(z.email().max(254)).max(10).default([]),
  /** Send the customer a confirmation, in the language they used. */
  inquiryAutoReply: z.boolean().default(false),
  whatsapp: z.string().max(40),
  /** The public site's design (issue #75). */
  templateId: TemplateIdSchema.default(DEFAULT_TEMPLATE_ID),
  branding: BrandingSchema.default(DEFAULT_BRANDING),
  localBusiness: LocalBusinessSchema.default(DEFAULT_LOCAL_BUSINESS),
  /** Which optional fields the quote form shows. Changing it needs the `lead_capture` flag. */
  inquiryFields: InquiryFieldsSchema.default(DEFAULT_INQUIRY_FIELDS),
  swatchNames: SwatchNamesSchema.default(DEFAULT_SWATCH_NAMES),
  socials: z.array(z.object({ platform: z.string().max(40), url: z.url() })).max(12),
  googleSiteVerification: z.string().max(120),
  twitterHandle: z.string().max(40),
})
  .refine((s) => s.enabledLocales.includes(s.primaryLocale), {
    path: ['primaryLocale'],
    message: 'The primary language must be enabled',
  })
  .refine((s) => new Set(s.enabledLocales).size === s.enabledLocales.length, {
    path: ['enabledLocales'],
    message: 'Each language can only be listed once',
  })
export type SiteSettings = z.infer<typeof SiteSettingsSchema>

export const NavigationSchema = z.object({
  header: z.object({ items: z.array(NavItemSchema).max(12), cta: LinkSchema.nullable() }),
  footer: z.object({
    eyebrow: LocalizedSchema,
    heading: LocalizedSchema,
    cta: LinkSchema.nullable(),
    columns: z.array(z.object({ title: LocalizedSchema, links: z.array(LinkSchema).max(20) })).max(4),
    legalLinks: z.array(LinkSchema).max(6),
  }),
})
export type Navigation = z.infer<typeof NavigationSchema>

// ── Leads ────────────────────────────────────────────────────────────────
export const InquiryStatusSchema = z.enum(['new', 'contacted', 'qualified', 'won', 'lost', 'spam'])
export type InquiryStatus = z.infer<typeof InquiryStatusSchema>

export const InquirySubmitSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.email().max(254),
  company: z.string().trim().max(160).default(''),
  role: z.string().trim().max(160).default(''),
  country: z.string().trim().max(80).default(''),
  productInterest: z.string().trim().max(160).default(''),
  quantity: z.string().trim().max(80).default(''),
  timeline: z.string().trim().max(80).default(''),
  message: z.string().trim().max(5000).default(''),
  locale: z.string().max(10).default('en'),
  sourcePath: z.string().max(300).default('/contact'),
  /** Cloudflare Turnstile token. Required once TURNSTILE_SECRET is configured. */
  turnstileToken: z.string().max(4096).optional(),
  /** Honeypot — must stay empty. */
  website: z.string().max(0).optional(),
  /** Attribution for the server-side Lead events (#30). */
  tracking: InquiryTrackingSchema.optional(),
})
export type InquirySubmit = z.infer<typeof InquirySubmitSchema>

export const InquirySchema = InquirySubmitSchema.omit({ turnstileToken: true, website: true, tracking: true }).extend({
  id: z.string(),
  status: InquiryStatusSchema,
  notes: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Inquiry = z.infer<typeof InquirySchema>


export const UpdateInquiryRequestSchema = z.object({
  status: InquiryStatusSchema.optional(),
  notes: z.string().max(10_000).optional(),
})

/** One audit-log entry as the admin shows it. */
export const ActivityItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  /** JSON with what else matters (e.g. the mailbox address); empty when nothing. */
  detail: z.string().default(''),
  userName: z.string().nullable(),
  createdAt: z.string(),
})
export type ActivityItem = z.infer<typeof ActivityItemSchema>

export const DashboardSchema = z.object({
  counts: z.object({
    pages: z.number().int(),
    products: z.number().int(),
    articles: z.number().int(),
    media: z.number().int(),
    newInquiries: z.number().int(),
    /** Online store only: variants at or under their low-stock mark. */
    lowStock: z.number().int().optional(),
  }),
  oldestNewInquiryAt: z.string().nullable(),
  recentActivity: z.array(ActivityItemSchema),
})
export type Dashboard = z.infer<typeof DashboardSchema>

/** GET /api/admin/dashboard/activity: the audit log, newest first. `mailbox`: only Inbox entries (admins only). */
export const ActivityFilterSchema = z.enum(['all', 'mailbox'])
export type ActivityFilter = z.infer<typeof ActivityFilterSchema>
export const ActivityListSchema = z.object({ items: z.array(ActivityItemSchema), hasMore: z.boolean() })
export type ActivityList = z.infer<typeof ActivityListSchema>

// ── Translation status (shown before switching the primary language) ────
export const TranslationStatusSchema = z.object({
  locale: LocaleSchema,
  /** Per content type: how many documents exist and how many have their own title in `locale`. */
  types: z.array(
    z.object({
      type: z.enum(['pages', 'product-categories', 'products', 'industries', 'articles', 'swatches']),
      total: z.number().int(),
      translated: z.number().int(),
    }),
  ),
})
export type TranslationStatus = z.infer<typeof TranslationStatusSchema>
